import { useEffect, useRef, useState } from "react";

import { styled } from "../../styles";
import { Checkbox } from "../Checkbox";
import { COMPARABLE_MEASUREMENTS, ComparableMeasurement } from "./mockData";

const Overlay = styled("div", {
  position: "absolute",
  top: "100%",
  right: 0,
  marginTop: "8px",
  zIndex: 3000,

  width: "280px",
  maxHeight: "400px",

  background: "white",
  borderRadius: "16px",
  boxShadow: "0px 0px 2px rgba(0, 0, 0, 0.22)",
  border: "1px solid var(--colors-gray-50)",
  overflow: "hidden",
});

const SearchContainer = styled("div", {
  padding: "16px 16px 0 16px",
});

const SearchInput = styled("input", {
  width: "100%",
  border: "none",
  borderBottom: "2px solid #5B5FC7",
  outline: "none",
  padding: "8px 0",
  fontSize: "14px",
  fontFamily: "$openSans",
  color: "$black",

  "&::placeholder": {
    color: "#999",
  },
});

const SearchIcon = styled("span", {
  position: "absolute",
  right: "20px",
  top: "24px",
  color: "#999",
  fontSize: "16px",
});

const List = styled("div", {
  padding: "8px 0",
  maxHeight: "300px",
  overflowY: "auto",
});

const ListItem = styled("div", {
  padding: "10px 16px",
  fontFamily: "$openSans",
  fontSize: "14px",
  color: "$black",

  "&:hover": {
    backgroundColor: "#F7F7F7",
  },

  variants: {
    disabled: {
      true: {
        opacity: 0.4,
        pointerEvents: "none",
      },
    },
  },
});

const UnitHint = styled("div", {
  padding: "8px 16px 4px",
  fontSize: "11px",
  fontFamily: "$openSans",
  color: "#6B778C",
  borderTop: "1px solid #EBECF0",
});

type CompareDropdownProps = {
  selectedKeys: string[];
  lockedUnit: string | null;
  onToggle: (key: string) => void;
  onClose: () => void;
};

export function CompareDropdown({
  selectedKeys,
  lockedUnit,
  onToggle,
  onClose,
}: CompareDropdownProps) {
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const filtered = COMPARABLE_MEASUREMENTS.filter((m: ComparableMeasurement) =>
    m.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Overlay ref={ref}>
      <SearchContainer>
        <SearchInput
          placeholder=""
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
        <SearchIcon>&#x1F50D;</SearchIcon>
      </SearchContainer>
      {lockedUnit && (
        <UnitHint>
          Showing measurements in <strong>{lockedUnit}</strong> only. Clear
          selection to compare a different unit.
        </UnitHint>
      )}
      <List>
        {filtered.map((m: ComparableMeasurement) => {
          const isSelected = selectedKeys.includes(m.key);
          const isUnitMismatch =
            !isSelected && lockedUnit !== null && m.unit !== lockedUnit;

          return (
            <ListItem key={m.key} disabled={isUnitMismatch}>
              <Checkbox
                label={`${m.label} (${m.unit})`}
                checked={isSelected}
                onChange={() => onToggle(m.key)}
                disabled={isUnitMismatch}
                labelPosition="after"
                spacing="12px"
              />
            </ListItem>
          );
        })}
      </List>
    </Overlay>
  );
}
